import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import ReactSelect from 'react-select';
import { Label, StyledInput, customSelectStyles } from '../styles/PackageIndexStyles';
import { formatWeightForDisplay, parseWeightParts } from '../utils/weightString';
import { resolveCardStatus } from '../utils/pidStatus';

/* ---------- utilities (safe for non-strings) ---------- */
const toText = (v) => String(v ?? '').trim();
const toLower = (v) => toText(v).toLowerCase();
const safeCompare = (a, b) =>
  toText(a).localeCompare(toText(b), undefined, { sensitivity: 'base', numeric: true });

const getUniqueOptions = (values) => {
  const flat = [];
  (values || []).forEach(v => {
    if (Array.isArray(v)) v.forEach(x => flat.push(toText(x)));
    else if (v && typeof v === 'object') flat.push(toText(v.label ?? v.value ?? JSON.stringify(v)));
    else flat.push(toText(v));
  });
  const unique = Array.from(new Set(flat.filter(Boolean)));
  unique.sort(safeCompare);
  return unique.map(v => ({ value: v, label: v }));
};

const excludeSelected = (options, selected) => {
  if (!selected?.length) return options;
  const taken = new Set(selected.map(s => toText(s.value)));
  return options.filter(o => !taken.has(toText(o.value)));
};

const matchesMultiSelect = (fieldValue, selected) => {
  if (!selected || selected.length === 0) return true;
  const selectedValues = selected.map(s => toLower(s.value));
  if (Array.isArray(fieldValue)) return fieldValue.some(v => selectedValues.includes(toLower(v)));
  return selectedValues.includes(toLower(fieldValue));
};

const flattenValues = (obj) => {
  const out = [];
  const walk = (v) => {
    if (Array.isArray(v)) v.forEach(walk);
    else if (v && typeof v === 'object') Object.values(v).forEach(walk);
    else if (v != null && v !== '') out.push(toLower(v));
  };
  walk(obj);
  return out;
};

const defaultFilter = {
  search: '',
  commodity: [],
  style: [],
  sizeOption: [],
  importSize: [],
  retailer: [],
  type: [],
  weight: [],
  status: 'Active',
};
/* ------------------------------------------------------ */

const SearchFilters = ({ activeTab, allItems, onFilterChange, bagFilters = null }) => {
  const [filtersByTab, setFiltersByTab] = useState({
    Methods: { ...defaultFilter },
    Bags: { ...defaultFilter },
    PLU: { ...defaultFilter },
    UPC: { ...defaultFilter },
    Displays: { ...defaultFilter },
  });
  const prevAllItemsRef = useRef(allItems);
  const prevActiveTabRef = useRef(activeTab);
  const isProcessingRef = useRef(false);

  const filter = filtersByTab[activeTab] || defaultFilter;

  // Lifecycle status filter (default Active). The browse fetches Active + Incoming; this
  // narrows the grid to one of them. `hasIncoming` is computed from the FULL set and gates
  // the dropdown — a tab with no Incoming records shows a disabled control locked on Active.
  const wantStatus = filter.status || 'Active';
  const hasIncoming = useMemo(
    () => (allItems || []).some((it) => resolveCardStatus(it) === 'Incoming'),
    [allItems]
  );
  // Item pool scoped to the selected status — drives the dependent option dropdowns so they
  // only offer values that exist within the currently-shown status.
  const statusScopedItems = useMemo(
    () => (allItems || []).filter((it) => resolveCardStatus(it) === wantStatus),
    [allItems, wantStatus]
  );

  // Message shown in selects when no options match the typed input
  const noOptionsMessage = useCallback(({ inputValue }) => (
    inputValue ? 'No results found' : 'No options'
  ), []);

  /* ---------- UPC field normalizers ---------- */
  const getUPCCommodity = useCallback((item) => toText(item?.Commodity ?? item?.CommodityName), []);
  const getUPCRetailer = useCallback((item) => toText(item?.Retailer), []);
  const getUPCWeight = useCallback((item) => formatWeightForDisplay(item?.Weight), []);

  const normalizeKey = useCallback((v) => toText(v).normalize('NFKC').replace(/\s+/g, ' ').toLowerCase(), []);
  const normalizeWeight = useCallback((v) => {
    const raw = toText(v);
    const parts = parseWeightParts(raw);
    const ct = parts.count ? String(Number(parts.count)) : '';
    const lb = parts.pounds ? String(Number(parts.pounds)) : '';
    if (ct || lb) return `ct:${ct}|lb:${lb}`;
    return normalizeKey(raw);
  }, [normalizeKey]);

  const matchesMultiSelectNormalized = useCallback((fieldValue, selected, normalizer) => {
    if (!selected || selected.length === 0) return true;
    const norm = normalizer || normalizeKey;
    const selectedValues = selected.map(s => norm(s.value));
    if (Array.isArray(fieldValue)) return fieldValue.some(v => selectedValues.includes(norm(v)));
    return selectedValues.includes(norm(fieldValue));
  }, [normalizeKey]);

  /* ---------- global filter ---------- */
  const filterItems = useCallback((filt) => {
    const searchText = toLower(filt.search);
    const wantStatusLocal = filt.status || 'Active';

    return (allItems || []).filter(item => {
      if (resolveCardStatus(item) !== wantStatusLocal) return false;
      const matchesSearch = !searchText || flattenValues(item).some(v => v.includes(searchText));

      if (activeTab === 'Methods') {
        return (
          matchesSearch &&
          matchesMultiSelect(item.Commodity, filt.commodity) &&
          matchesMultiSelect(item.SizeOption, filt.sizeOption) &&
          matchesMultiSelect(item.Retailer, filt.retailer) &&
          matchesMultiSelect(item.Style, filt.style) &&
          matchesMultiSelect(item.Type, filt.type)
        );
      }

      if (activeTab === 'Bags') {
        const items = item.Items || [];
        const passesDropdowns = items.some(sub =>
          matchesMultiSelect(sub.Commodity ?? sub.CommodityName, filt.commodity) &&
          matchesMultiSelect(sub.Retailer, filt.retailer) &&
          matchesMultiSelect(sub.Type, filt.type)
        );
        return matchesSearch && passesDropdowns;
      }

      if (activeTab === 'PLU') {
        return (
          matchesSearch &&
          matchesMultiSelect(item.Commodity, filt.commodity)
        );
      }

      if (activeTab === 'UPC') {
        const commodity = getUPCCommodity(item);
        const retailer  = getUPCRetailer(item);
        const weight    = getUPCWeight(item);

        return (
          matchesSearch &&
          matchesMultiSelectNormalized(commodity, filt.commodity, normalizeKey) &&
          matchesMultiSelectNormalized(retailer,  filt.retailer,  normalizeKey) &&
          matchesMultiSelectNormalized(weight,    filt.weight,    normalizeWeight)
        );
      }

      if (activeTab === 'Displays') {
        return matchesSearch;
      }

      return matchesSearch;
    });
  }, [
    allItems,
    activeTab,
    getUPCCommodity,
    getUPCRetailer,
    getUPCWeight,
    matchesMultiSelectNormalized,
    normalizeKey,
    normalizeWeight,
  ]);

  /* ---------- controlled changes ---------- */
  const handleChange = useCallback((key, value) => {
    const newValue =
      key === 'search' ? value
      : key === 'status' ? (value?.value || 'Active')
      : (value || []);
    const next = {
      ...filtersByTab,
      [activeTab]: { ...filtersByTab[activeTab], [key]: newValue },
    };
    setFiltersByTab(next);
    onFilterChange(filterItems(next[activeTab]));
  }, [filtersByTab, activeTab, filterItems, onFilterChange]);

  useEffect(() => {
    // Only trigger when allItems reference actually changes or tab changes
    const allItemsChanged = prevAllItemsRef.current !== allItems;
    const tabChanged = prevActiveTabRef.current !== activeTab;
    
    if (allItemsChanged || tabChanged) {
      // Prevent infinite loops - don't process if already processing
      if (isProcessingRef.current) return;
      
      isProcessingRef.current = true;
      prevAllItemsRef.current = allItems;
      prevActiveTabRef.current = activeTab;
      
      if (allItems && allItems.length >= 0 && filterItems && onFilterChange) {
        const filtered = filterItems(filter);
        onFilterChange(filtered);
      }
      
      // Use requestAnimationFrame to reset flag after render completes
      requestAnimationFrame(() => {
        isProcessingRef.current = false;
      });
    }
    // filterItems depends on allItems, onFilterChange is memoized and stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allItems, activeTab, filter]); // Include allItems but use ref guard to prevent loops

  /* ---------- dynamic options: built from CURRENT filtered context (AND logic) ---------- */
  // Helper: Methods - filter items by all active selections except the excluded key
  const getMethodItemsExcluding = useCallback((excludeKey) => {
    if (activeTab !== 'Methods') return [];
    const f = filter;
    const searchText = toLower(f.search);
    return statusScopedItems.filter(item => {
      const matchesSearch = !searchText || flattenValues(item).some(v => v.includes(searchText));
      const checks = [];
      checks.push(matchesSearch);
      if (excludeKey !== 'commodity') checks.push(matchesMultiSelect(item.Commodity, f.commodity));
      if (excludeKey !== 'sizeOption') checks.push(matchesMultiSelect(item.SizeOption, f.sizeOption));
      if (excludeKey !== 'retailer') checks.push(matchesMultiSelect(item.Retailer, f.retailer));
      if (excludeKey !== 'style') checks.push(matchesMultiSelect(item.Style, f.style));
      if (excludeKey !== 'type') checks.push(matchesMultiSelect(item.Type, f.type));
      return checks.every(Boolean);
    });
  }, [activeTab, statusScopedItems, filter]);

  const methodCommodityOptions = useMemo(() => {
    if (activeTab !== 'Methods') return [];
    const items = getMethodItemsExcluding('commodity');
    return getUniqueOptions(items.map(x => x.Commodity));
  }, [activeTab, getMethodItemsExcluding]);
  
  const methodRetailerOptions = useMemo(() => {
    if (activeTab !== 'Methods') return [];
    const items = getMethodItemsExcluding('retailer');
    return getUniqueOptions(items.map(x => x.Retailer));
  }, [activeTab, getMethodItemsExcluding]);

  const methodStyleOptions = useMemo(() => {
    if (activeTab !== 'Methods') return [];
    const items = getMethodItemsExcluding('style');
    return getUniqueOptions(items.map(x => x.Style));
  }, [activeTab, getMethodItemsExcluding]);

  const methodTypeOptions = useMemo(() => {
    if (activeTab !== 'Methods') return [];
    const items = getMethodItemsExcluding('type');
    return getUniqueOptions(items.map(x => x.Type));
  }, [activeTab, getMethodItemsExcluding]);

  // Helper: Bags - flatten group.Items and filter subs by all active selections except the excluded key
  const getBagSubsExcluding = useCallback((excludeKey) => {
    if (activeTab !== 'Bags') return [];
    const f = filter;
    const searchText = toLower(f.search);
    const subs = [];
    statusScopedItems.forEach(g => {
      const groupMatchesSearch = !searchText || flattenValues(g).some(v => v.includes(searchText));
      if (!groupMatchesSearch) return;
      (g.Items || []).forEach(it => subs.push(it));
    });
    return subs.filter(sub => {
      const subCommodity = sub.Commodity ?? sub.CommodityName;
      const checks = [];
      if (excludeKey !== 'commodity') checks.push(matchesMultiSelect(subCommodity, f.commodity));
      if (excludeKey !== 'retailer') checks.push(matchesMultiSelect(sub.Retailer, f.retailer));
      if (excludeKey !== 'type') checks.push(matchesMultiSelect(sub.Type, f.type));
      return checks.every(Boolean);
    });
  }, [activeTab, statusScopedItems, filter]);

  const bagCommodityOptions = useMemo(() => {
    if (activeTab !== 'Bags') return [];
    const subs = getBagSubsExcluding('commodity');
    return getUniqueOptions(subs.map(it => it.Commodity ?? it.CommodityName));
  }, [activeTab, getBagSubsExcluding]);

  const bagRetailerOptions = useMemo(() => {
    if (activeTab !== 'Bags') return [];
    const subs = getBagSubsExcluding('retailer');
    return getUniqueOptions(subs.map(it => it.Retailer));
  }, [activeTab, getBagSubsExcluding]);

  const bagTypeOptions = useMemo(() => {
    if (activeTab !== 'Bags') return [];
    const subs = getBagSubsExcluding('type');
    return getUniqueOptions(subs.map(it => it.Type));
  }, [activeTab, getBagSubsExcluding]);

  // Helper: PLU - filter by all active selections except the excluded key
  const getPLUItemsExcluding = useCallback((excludeKey) => {
    if (activeTab !== 'PLU') return [];
    const f = filter;
    const searchText = toLower(f.search);
    return statusScopedItems.filter(item => {
      const matchesSearch = !searchText || flattenValues(item).some(v => v.includes(searchText));

      const checks = [];
      checks.push(matchesSearch);
      if (excludeKey !== 'commodity') checks.push(matchesMultiSelect(item.Commodity, f.commodity));
      return checks.every(Boolean);
    });
  }, [activeTab, statusScopedItems, filter]);

  const pluCommodityOptions = useMemo(() => {
    if (activeTab !== 'PLU') return [];
    const items = getPLUItemsExcluding('commodity');
    return getUniqueOptions(items.map(x => x.Commodity));
  }, [activeTab, getPLUItemsExcluding]);

  // NOTE: PLU filters intentionally limited to Search + Commodity

  // UPC: independent options built from the entire UPC dataset (ignore current selections/search)
  const upcCommodityOptions = useMemo(() => {
    if (activeTab !== 'UPC') return [];
    const items = statusScopedItems;
    return getUniqueOptions(items.map(x => getUPCCommodity(x)));
  }, [activeTab, statusScopedItems, getUPCCommodity]);

  const upcRetailerOptions = useMemo(() => {
    if (activeTab !== 'UPC') return [];
    const items = statusScopedItems;
    return getUniqueOptions(items.map(x => getUPCRetailer(x)));
  }, [activeTab, statusScopedItems, getUPCRetailer]);

  const upcWeightOptions = useMemo(() => {
    if (activeTab !== 'UPC') return [];
    const items = statusScopedItems;
    return getUniqueOptions(items.map(x => getUPCWeight(x)));
  }, [activeTab, statusScopedItems, getUPCWeight]);

  /* ---------- UI ---------- */
  const clearFilters = () => {
    const cleared = { ...defaultFilter };
    const next = { ...filtersByTab, [activeTab]: cleared };
    setFiltersByTab(next);
    onFilterChange(filterItems(cleared));
  };

  return (
    <div>
      {/* Search + Clear */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Label style={{ margin: 0 }}>Search</Label>
        <button
          onClick={clearFilters}
          style={{
            backgroundColor: 'var(--green-600)',
            color: 'var(--white)',
            border: 'none',
            borderRadius: '6px',
            padding: '0.15rem 0.5rem',
            fontSize: '0.85rem',
            cursor: 'pointer',
            flexShrink: 0,
            transition: 'background-color 0.2s ease',
          }}
          onMouseOver={e => (e.currentTarget.style.backgroundColor = 'var(--green-700)')}
          onMouseOut={e => (e.currentTarget.style.backgroundColor = 'var(--green-600)')}
        >
          Clear
        </button>
      </div>

      <StyledInput
        value={filter.search}
        onChange={e => handleChange('search', e.target.value)}
        placeholder="Search"
      />

      {activeTab === 'Methods' && (
        <>
          <Label>Commodity</Label>
          <ReactSelect
            isMulti
            styles={customSelectStyles}
            noOptionsMessage={noOptionsMessage}
            options={excludeSelected(methodCommodityOptions, filter.commodity)}
            value={filter.commodity}
            onChange={val => handleChange('commodity', val)}
            isClearable
          />
          <Label>Retailer</Label>
          <ReactSelect
            isMulti
            styles={customSelectStyles}
            noOptionsMessage={noOptionsMessage}
            options={excludeSelected(methodRetailerOptions, filter.retailer)}
            value={filter.retailer}
            onChange={val => handleChange('retailer', val)}
            isClearable
          />
          <Label>Style</Label>
          <ReactSelect
            isMulti
            styles={customSelectStyles}
            noOptionsMessage={noOptionsMessage}
            options={excludeSelected(methodStyleOptions, filter.style)}
            value={filter.style}
            onChange={val => handleChange('style', val)}
            isClearable
          />
          <Label>Type</Label>
          <ReactSelect
            isMulti
            styles={customSelectStyles}
            noOptionsMessage={noOptionsMessage}
            options={excludeSelected(methodTypeOptions, filter.type)}
            value={filter.type}
            onChange={val => handleChange('type', val)}
            isClearable
          />
        </>
      )}

      {activeTab === 'Bags' && (
        <>
          <Label>Commodity</Label>
          <ReactSelect
            isMulti
            styles={customSelectStyles}
            noOptionsMessage={noOptionsMessage}
            options={excludeSelected(bagCommodityOptions, filter.commodity)}
            value={filter.commodity}
            onChange={val => handleChange('commodity', val)}
            isClearable
          />
          <Label>Retailer</Label>
          <ReactSelect
            isMulti
            styles={customSelectStyles}
            noOptionsMessage={noOptionsMessage}
            options={excludeSelected(bagRetailerOptions, filter.retailer)}
            value={filter.retailer}
            onChange={val => handleChange('retailer', val)}
            isClearable
          />
          <Label>Type</Label>
          <ReactSelect
            isMulti
            styles={customSelectStyles}
            noOptionsMessage={noOptionsMessage}
            options={excludeSelected(bagTypeOptions, filter.type)}
            value={filter.type}
            onChange={val => handleChange('type', val)}
            isClearable
          />
        </>
      )}

      {activeTab === 'PLU' && (
        <>
          <Label>Commodity</Label>
          <ReactSelect
            isMulti
            styles={customSelectStyles}
            noOptionsMessage={noOptionsMessage}
            options={excludeSelected(pluCommodityOptions, filter.commodity)}
            value={filter.commodity}
            onChange={val => handleChange('commodity', val)}
            isClearable
          />
        </>
      )}

      {activeTab === 'UPC' && (
        <>
          <Label>Commodity</Label>
          <ReactSelect
            isMulti
            styles={customSelectStyles}
            noOptionsMessage={noOptionsMessage}
            options={excludeSelected(upcCommodityOptions, filter.commodity)}
            value={filter.commodity}
            onChange={val => handleChange('commodity', val)}
            isClearable
          />
          <Label>Retailer</Label>
          <ReactSelect
            isMulti
            styles={customSelectStyles}
            noOptionsMessage={noOptionsMessage}
            options={excludeSelected(upcRetailerOptions, filter.retailer)}
            value={filter.retailer}
            onChange={val => handleChange('retailer', val)}
            isClearable
          />
          <Label>Count / Weight</Label>
          <ReactSelect
            isMulti
            styles={customSelectStyles}
            noOptionsMessage={noOptionsMessage}
            options={excludeSelected(upcWeightOptions, filter.weight)}
            value={filter.weight}
            onChange={val => handleChange('weight', val)}
            isClearable
          />
        </>
      )}

      {activeTab === 'Displays' && (
        <>
          {/* Displays currently support only text search. Additional filters can be added later. */}
        </>
      )}

      {/* Status — always the last filter. Default Active; switch to Incoming only when the
          tab has Incoming records, otherwise the control is disabled (locked on Active). */}
      <Label>Status</Label>
      <ReactSelect
        styles={customSelectStyles}
        options={
          hasIncoming
            ? [{ value: 'Active', label: 'Active' }, { value: 'Incoming', label: 'Incoming' }]
            : [{ value: 'Active', label: 'Active' }]
        }
        value={{
          value: hasIncoming ? wantStatus : 'Active',
          label: hasIncoming ? wantStatus : 'Active',
        }}
        onChange={val => handleChange('status', val)}
        isDisabled={!hasIncoming}
        isClearable={false}
        isSearchable={false}
      />
    </div>
  );
};

export default React.memo(SearchFilters);
